import React, {createContext, useEffect, useState} from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import API_BASE_URL from '../config';

export const ShopContext = createContext(null);

const getDefaultCart = () => {
    return {};
}

const getCartKey = (itemId, size = '') => {
    const numericId = Number(itemId);
    return size && size !== '' ? `${numericId}-${size}` : numericId.toString();
}

const getProductIdFromKey = (cartKey) => {
    return parseInt(cartKey.split('-')[0]);
}

const getSizeFromKey = (cartKey) => {
    const parts = cartKey.split('-');
    return parts.length > 1 ? parts[1] : '';
}

const ShopContextProvider = (props) => {
    const [all_product, setAll_Product] = useState([]); 
    const [allProducts, setAllProducts] = useState([]);
    const [cartItems, setCartItems] = useState(getDefaultCart());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cartCount, setCartCount] = useState(0);
    const [wishlist, setWishlist] = useState([]);
    const [recentlyViewed, setRecentlyViewed] = useState([]);

    const showMessage = (text, type = 'success') => {
        const options = {
            duration: 4000,
        };

        if (type === 'success') {
            toast.success(text, options);
        } else if (type === 'error') {
            toast.error(text, options);
        } else if (type === 'warning') {
            toast.error(text, options);
        } else {
            toast(text, options);
        }
    };

    // Function to fetch cart count
    const fetchCartCount = async () => {
        try {
            if (localStorage.getItem('auth-token')) {
                const response = await fetch(`${API_BASE_URL}/getcart`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'auth-token': localStorage.getItem('auth-token'),
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({}),
                });
                
                if (response.ok) {
                    const cartData = await response.json();
                    let count = 0;
                    Object.values(cartData.cartData || {}).forEach(qty => {
                        count += qty;
                    });
                    setCartCount(count);
                }
            } else {
                const sessionId = localStorage.getItem('guest-session-id');
                if (sessionId) {
                    const response = await fetch(`${API_BASE_URL}/getcart`, {
                        method: 'POST',
                        headers: {
                            'Accept': 'application/json',
                            'session-id': sessionId,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({}),
                    });
                    
                    if (response.ok) {
                        const cartData = await response.json();
                        let count = 0;
                        Object.values(cartData.cartData || {}).forEach(qty => {
                            count += qty;
                        });
                        setCartCount(count);
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching cart count:', error);
        }
    };

    // Fetch all products for search
    const fetchAllProductsForSearch = async () => {
        try {
            console.log("Fetching all products for search...");
            
            const response = await fetch(`${API_BASE_URL}/allproducts?limit=200`);
            
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }
            
            const data = await response.json();
            console.log("Search products API Response:", data);
            
            let productsArray = [];
            
            if (Array.isArray(data)) {
                productsArray = data;
                console.log("Using old format (direct array) for search");
            } else if (data && Array.isArray(data.products)) {
                productsArray = data.products;
                console.log("Using new format (object with products property) for search");
            } else if (data && data.success && Array.isArray(data.products)) {
                productsArray = data.products;
                console.log("Using success format for search");
            } else {
                console.error("Unexpected search products format:", data);
                productsArray = [];
            }
            
            console.log(`Search products loaded: ${productsArray.length} items`);
            setAllProducts(productsArray);
            return productsArray;
            
        } catch (err) {
            console.error("Error fetching search products:", err);
            setAllProducts([]);
            showMessage('Failed to load search data. Using fallback search.', 'warning');
            return [];
        }
    };

    // Fetch products for the shop
    const fetchShopProducts = async () => {
        try {
            console.log("Fetching products from /allproducts...");
            const response = await fetch(`${API_BASE_URL}/allproducts`);
            
            if (!response.ok) {
                throw new Error(`Server responded with status: ${response.status}`);
            }
            
            const data = await response.json();
            console.log("API Response:", data);
            
            let productsArray = [];
            
            if (Array.isArray(data)) {
                productsArray = data;
                console.log("Using old format (direct array)");
            } else if (data && Array.isArray(data.products)) {
                productsArray = data.products;
                console.log("Using new format (object with products property)");
            } else if (data && data.success && Array.isArray(data.products)) {
                productsArray = data.products;
                console.log("Using alternative new format");
            } else {
                console.error("Unexpected data format:", data);
                productsArray = [];
            }
            
            console.log(`Shop products loaded: ${productsArray.length} items`);
            setAll_Product(productsArray);
            return productsArray;
            
        } catch (err) {
            console.error("Error fetching shop products:", err);
            setError(`Backend connection failed: ${err.message}`);
            setAll_Product([]);
            showMessage('Failed to load products. Please check your connection.', 'error');
            return [];
        }
    };

    // Client-side search function
    const performClientSideSearch = (query) => {
        if (!allProducts || allProducts.length === 0) {
            console.log("No products available for client-side search");
            return [];
        }
        
        const lowerQuery = query.toLowerCase();
        console.log(`Performing client-side search for: "${query}"`);
        
        const results = allProducts.filter(product => {
            const name = product.name?.toLowerCase() || '';
            const category = product.category?.toLowerCase() || '';
            const brand = product.brand?.toLowerCase() || '';
            const description = product.description?.toLowerCase() || '';
            
            return (
                name.includes(lowerQuery) ||
                category.includes(lowerQuery) ||
                description.includes(lowerQuery)
            );
        });
        
        console.log(`Client-side search found ${results.length} results`);
        return results.slice(0, 10);
    };

    // Search products via API
    const searchProducts = async (query) => {
        try {
            console.log(`Searching products via API: "${query}"`);
            
            if (!query || query.trim().length < 1) {
                return [];
            }
            
            const response = await fetch(`${API_BASE_URL}/api/products/search?q=${encodeURIComponent(query.trim())}`);
            
            if (!response.ok) {
                console.log("Search API failed, using client-side search");
                return performClientSideSearch(query);
            }
            
            const data = await response.json();
            
            if (data.success) {
                console.log(`API search found ${data.products?.length || 0} results`);
                return data.products || [];
            } else {
                console.log("Search API returned error, using client-side search");
                return performClientSideSearch(query);
            }
            
        } catch (error) {
            console.error("Error in searchProducts:", error);
            return performClientSideSearch(query);
        }
    };

    // Get quick search suggestions
    const getSearchSuggestions = async (query) => {
        try {
            if (!query || query.trim().length < 2) {
                return [];
            }
            
            const response = await fetch(`${API_BASE_URL}/api/products/quick-search?q=${encodeURIComponent(query.trim())}`);
            
            if (response.ok) {
                const data = await response.json();
                return data.suggestions || [];
            }
            return [];
        } catch (error) {
            console.error("Error fetching search suggestions:", error);
            return [];
        }
    };

    const fetchUserCart = async (products) => {
        try {
            let cartData = {};
            
            if(localStorage.getItem('auth-token')){
                console.log("Fetching authenticated user cart...");
                const response = await fetch(`${API_BASE_URL}/getcart`,{
                    method:'POST',
                    headers:{
                        'Accept':'application/json',
                        'auth-token':`${localStorage.getItem('auth-token')}`,
                        'Content-Type':'application/json',
                    },
                    body: JSON.stringify({}),
                });
                
                if (response.ok) {
                    const result = await response.json();
                    cartData = result.cartData || {};
                    console.log("Authenticated cart data received:", cartData);
                }
            } else {
                console.log("No auth token, checking guest cart...");
                let sessionId = localStorage.getItem('guest-session-id');
                
                if (sessionId) {
                    const response = await fetch(`${API_BASE_URL}/getcart`, {
                        method: 'POST',
                        headers: {
                            'Accept': 'application/json',
                            'session-id': sessionId,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({}),
                    });
                    
                    if (response.ok) {
                        const result = await response.json();
                        cartData = result.cartData || {};
                        console.log("Guest cart data received:", cartData);
                    }
                } else {
                    const sessionResponse = await fetch(`${API_BASE_URL}/api/guest/session`, {
                        method: 'POST',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json'
                        }
                    });
                    
                    if (sessionResponse.ok) {
                        const sessionData = await sessionResponse.json();
                        if (sessionData.success) {
                            sessionId = sessionData.sessionId;
                            localStorage.setItem('guest-session-id', sessionId);
                            console.log("New guest session created:", sessionId);
                        }
                    }
                }
            }
            
            const validatedCart = {};
            
            Object.keys(cartData).forEach(cartKey => {
                const productId = getProductIdFromKey(cartKey);
                
                const productExists = products.some(product => 
                    product.id === productId
                );
                
                if (productExists && cartData[cartKey] > 0) {
                    validatedCart[cartKey] = cartData[cartKey];
                }
            });
            
            console.log("Validated cart:", validatedCart);
            setCartItems(validatedCart);
            
            let count = 0;
            Object.values(validatedCart).forEach(qty => {
                count += qty;
            });
            setCartCount(count);
            
        } catch (err) {
            console.error("Error fetching cart:", err);
            setCartItems(getDefaultCart());
            showMessage('Failed to load cart data.', 'error');
        }
    };

    // NEW: Fetch wishlist
    const fetchWishlist = async () => {
        try {
            if (localStorage.getItem('auth-token')) {
                const response = await fetch(`${API_BASE_URL}/api/wishlist`, {
                    method: 'GET',
                    headers: {
                        'auth-token': localStorage.getItem('auth-token'),
                    },
                });
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.success) {
                        setWishlist(data.wishlist || []);
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching wishlist:', error);
        }
    };

    // NEW: Add to wishlist
    const addToWishlist = async (productId) => {
        try {
            if (localStorage.getItem('auth-token')) {
                const response = await fetch(`${API_BASE_URL}/api/wishlist/add`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': localStorage.getItem('auth-token'),
                    },
                    body: JSON.stringify({ productId }),
                });
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.success) {
                        setWishlist(prev => [...prev, productId]);
                        showMessage('Added to wishlist!', 'success');
                        return true;
                    }
                }
            } else {
                showMessage('Please login to add items to wishlist', 'warning');
            }
            return false;
        } catch (error) {
            console.error('Error adding to wishlist:', error);
            return false;
        }
    };

    // NEW: Remove from wishlist
    const removeFromWishlist = async (productId) => {
        try {
            if (localStorage.getItem('auth-token')) {
                const response = await fetch(`${API_BASE_URL}/api/wishlist/remove`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': localStorage.getItem('auth-token'),
                    },
                    body: JSON.stringify({ productId }),
                });
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.success) {
                        setWishlist(prev => prev.filter(id => id !== productId));
                        showMessage('Removed from wishlist', 'info');
                        return true;
                    }
                }
            }
            return false;
        } catch (error) {
            console.error('Error removing from wishlist:', error);
            return false;
        }
    };

    // NEW: Check if product is in wishlist
    const isInWishlist = (productId) => {
        return wishlist.includes(productId);
    };

    // NEW: Add to recently viewed
    const addToRecentlyViewed = (product) => {
        try {
            const current = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
            const filtered = current.filter(p => p.id !== product.id);
            const updated = [product, ...filtered].slice(0, 10);
            localStorage.setItem('recentlyViewed', JSON.stringify(updated));
            setRecentlyViewed(updated);
        } catch (error) {
            console.error('Error adding to recently viewed:', error);
        }
    };

    // Initialize the app
    useEffect(() => {
        const initializeApp = async () => {
            try {
                console.log("Initializing app...");
                
                const shopProducts = await fetchShopProducts();
                setAllProducts(shopProducts);
                console.log(`Search products initialized with ${shopProducts.length} items`);
                
                await fetchUserCart(shopProducts);
                await fetchWishlist();
                
                // Load recently viewed
                const stored = localStorage.getItem('recentlyViewed');
                if (stored) {
                    setRecentlyViewed(JSON.parse(stored));
                }
                
                console.log("App initialized successfully");
            } catch (error) {
                console.error("App initialization failed:", error);
                showMessage('Failed to initialize app. Please refresh the page.', 'error');
            } finally {
                console.log("Loading complete");
                setLoading(false);
            }
        };

        initializeApp();
    }, []);

    // Refresh all products
    const refreshAllProducts = async () => {
        try {
            console.log("Refreshing all products...");
            setLoading(true);
            
            const shopProducts = await fetchShopProducts();
            setAllProducts(shopProducts);
            
            showMessage('Products refreshed successfully!', 'success');
        } catch (error) {
            console.error('Error refreshing products:', error);
            showMessage('Failed to refresh products', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Add to cart function
    const addToCart = async (itemId, size = '', color = '') => {
        try {
            const numericItemId = Number(itemId);
            const product = all_product.find(p => p.id === numericItemId);
            
            if (!product) {
                showMessage('Product not found. Please try again.', 'error');
                return false;
            }

            let response;
            let cartKey = getCartKey(numericItemId, size);
            
            if (localStorage.getItem('auth-token')) {
                response = await fetch(`${API_BASE_URL}/addtocart`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'auth-token': localStorage.getItem('auth-token')
                    },
                    body: JSON.stringify({ 
                        itemId: numericItemId,
                        size: size,
                        color: color
                    })
                });
            } else {
                let sessionId = localStorage.getItem('guest-session-id');
                if (!sessionId) {
                    const sessionResponse = await fetch(`${API_BASE_URL}/api/guest/session`, {
                        method: 'POST',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json'
                        }
                    });
                    const sessionData = await sessionResponse.json();
                    if (sessionData.success) {
                        sessionId = sessionData.sessionId;
                        localStorage.setItem('guest-session-id', sessionId);
                    }
                }
                
                response = await fetch(`${API_BASE_URL}/addtocart`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'session-id': sessionId
                    },
                    body: JSON.stringify({ 
                        itemId: numericItemId,
                        size: size,
                        color: color
                    })
                });
            }
            
            const data = await response.json();
            
            if (data.success) {
                setCartItems(prev => {
                    const currentQuantity = prev[cartKey] || 0;
                    const newQuantity = currentQuantity + 1;
                    const updatedCart = { 
                        ...prev, 
                        [cartKey]: newQuantity 
                    };
                    
                    setCartCount(prevCount => prevCount + 1);
                    
                    return updatedCart;
                });
                
                showMessage(`${product.name} (Size: ${size || 'One Size'}) has been added to cart!`, 'success');
                return true;
            } else {
                showMessage(data.error || 'Failed to add item to cart. Please try again.', 'error');
                return false;
            }
            
        } catch (error) {
            console.error('Error adding to cart:', error);
            showMessage('An error occurred while adding to cart', 'error');
            return false;
        }
    };

    const removeFromCart = (itemId, size = '') => {
        const numericItemId = Number(itemId);
        const cartKey = getCartKey(numericItemId, size);
        const product = all_product.find(p => p.id === numericItemId);
        
        setCartItems((prev) => {
            const currentQuantity = prev[cartKey] || 0;
            
            if (currentQuantity <= 0) {
                return prev;
            }
            
            const newQuantity = Math.max(0, currentQuantity - 1);
            const updatedCart = { ...prev };
            
            if (newQuantity === 0) {
                delete updatedCart[cartKey];
                setCartCount(prevCount => Math.max(0, prevCount - 1));
            } else {
                updatedCart[cartKey] = newQuantity;
            }

            const updateBackend = async () => {
                try {
                    if (localStorage.getItem('auth-token')) {
                        await fetch(`${API_BASE_URL}/removefromcart`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'auth-token': localStorage.getItem('auth-token'),
                            },
                            body: JSON.stringify({ 
                                itemId: numericItemId,
                                size: size
                            }),
                        });
                    } else {
                        const sessionId = localStorage.getItem('guest-session-id');
                        if (sessionId) {
                            await fetch(`${API_BASE_URL}/removefromcart`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'session-id': sessionId,
                                },
                                body: JSON.stringify({ 
                                    itemId: numericItemId,
                                    size: size
                                }),
                            });
                        }
                    }
                    
                    if (product) {
                        if (newQuantity === 0) {
                            showMessage(`${product.name} (Size: ${size || 'One Size'}) removed from cart`, 'info');
                        } else {
                            showMessage(`Quantity updated for ${product.name} (Size: ${size || 'One Size'})`, 'info');
                        }
                    }
                } catch (err) {
                    showMessage('Failed to update cart. Please try again.', 'error');
                }
            };
            
            updateBackend();
            
            return updatedCart;
        });
    };

    const getTotalCartAmount = () => {
        if (!cartItems || !all_product.length) return 0;
        
        let totalAmount = 0;
        
        for(const cartKey in cartItems) {
            const quantity = cartItems[cartKey];
            if(quantity > 0) {
                const productId = getProductIdFromKey(cartKey);
                const itemInfo = all_product.find((product) => product.id === productId);
                
                if (itemInfo && itemInfo.new_price) {
                    totalAmount += itemInfo.new_price * quantity;
                }
            }
        }
        
        return totalAmount;
    };

    const getTotalCartItems = () => {
        return cartCount;
    };

    const getCartItemsDetails = () => {
        if (!cartItems || !all_product.length) {
            return [];
        }
        
        const cartDetails = [];
        
        for(const cartKey in cartItems) {
            const quantity = cartItems[cartKey];
            if(quantity > 0) {
                const productId = getProductIdFromKey(cartKey);
                const itemInfo = all_product.find((product) => product.id === productId);
                
                if (itemInfo) {
                    const size = getSizeFromKey(cartKey);
                    
                    cartDetails.push({
                        product: itemInfo,
                        quantity: quantity,
                        size: size,
                        cartKey: cartKey
                    });
                }
            }
        }
        
        return cartDetails;
    };

    const getCartItemQuantity = (itemId, size = '') => {
        const cartKey = getCartKey(itemId, size);
        const quantity = cartItems[cartKey] || 0;
        return quantity;
    };

    const updateCartFromBackend = async () => {
        try {
            let response;
            
            if (localStorage.getItem('auth-token')) {
                response = await fetch(`${API_BASE_URL}/getcart`, {
                    method:'POST',
                    headers:{
                        'Accept':'application/json',
                        'auth-token': localStorage.getItem('auth-token'),
                        'Content-Type':'application/json',
                    },
                    body: JSON.stringify({}),
                });
            } else {
                const sessionId = localStorage.getItem('guest-session-id');
                if (sessionId) {
                    response = await fetch(`${API_BASE_URL}/getcart`, {
                        method: 'POST',
                        headers: {
                            'Accept': 'application/json',
                            'session-id': sessionId,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({}),
                    });
                }
            }
            
            if (response && response.ok) {
                const result = await response.json();
                const cartData = result.cartData || {};
                const validatedCart = {};
                
                Object.keys(cartData).forEach(cartKey => {
                    const productId = getProductIdFromKey(cartKey);
                    const productExists = all_product.some(product => 
                        product.id === productId
                    );
                    
                    if (productExists && cartData[cartKey] > 0) {
                        validatedCart[cartKey] = cartData[cartKey];
                    }
                });
                
                setCartItems(validatedCart);
                
                let count = 0;
                Object.values(validatedCart).forEach(qty => {
                    count += qty;
                });
                setCartCount(count);
                
                showMessage('Cart updated successfully!', 'success');
            }
        } catch (err) {
            showMessage('Failed to update cart. Please try again.', 'error');
        }
    };

    const clearCart = async () => {
        try {
            setCartItems(getDefaultCart());
            setCartCount(0);
            
            if (localStorage.getItem('auth-token')) {
                const response = await fetch(`${API_BASE_URL}/clearcart`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': localStorage.getItem('auth-token')
                    }
                });
                
                const data = await response.json();
                if (!data.success) {
                    throw new Error('Backend clearance failed');
                }
            } else {
                const sessionId = localStorage.getItem('guest-session-id');
                if (sessionId) {
                    const response = await fetch(`${API_BASE_URL}/clearcart`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'session-id': sessionId
                        }
                    });
                    
                    const data = await response.json();
                    if (!data.success) {
                        throw new Error('Backend clearance failed');
                    }
                }
            }
            
            showMessage('Cart cleared successfully!', 'info');
            return true;
        } catch (error) {
            showMessage('Failed to clear cart. Please try again.', 'error');
            return false;
        }
    };

    // Place order function
    const placeOrder = async (orderData) => {
        try {
            let headers = {
                'Content-Type': 'application/json'
            };
            
            if (localStorage.getItem('auth-token')) {
                headers['auth-token'] = localStorage.getItem('auth-token');
            } else {
                const sessionId = localStorage.getItem('guest-session-id');
                if (sessionId) {
                    headers['session-id'] = sessionId;
                }
            }
            
            const response = await fetch(`${API_BASE_URL}/api/order/createorder`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(orderData)
            });

            const data = await response.json();
            
            if (data.success) {
                await clearCart();
                return { success: true, orderId: data.orderId, userType: data.userType };
            } else {
                throw new Error(data.message || 'Failed to place order');
            }
        } catch (error) {
            console.error('Error placing order:', error);
            throw error;
        }
    };

    const clearError = () => {
        setError(null);
    };

    // Get detailed cart items with product info
    const getDetailedCartItems = async () => {
        try {
            let response;
            
            if (localStorage.getItem('auth-token')) {
                response = await fetch(`${API_BASE_URL}/getcartdetails`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'auth-token': localStorage.getItem('auth-token'),
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({}),
                });
            } else {
                const sessionId = localStorage.getItem('guest-session-id');
                if (sessionId) {
                    response = await fetch(`${API_BASE_URL}/getcartdetails`, {
                        method: 'POST',
                        headers: {
                            'Accept': 'application/json',
                            'session-id': sessionId,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({}),
                    });
                }
            }
            
            if (response && response.ok) {
                const data = await response.json();
                return data.cartItems || [];
            }
            return [];
        } catch (error) {
            console.error('Error fetching detailed cart:', error);
            return [];
        }
    };

    // Function to get product by ID
    const getProductById = (id) => {
        const numericId = Number(id);
        return all_product.find(product => product.id === numericId);
    };

    // Function to get products by category
    const getProductsByCategory = (category) => {
        return all_product.filter(product => 
            product.category.toLowerCase() === category.toLowerCase() ||
            (product.category_slug && product.category_slug === category)
        );
    };

    // Function to get latest products
    const getLatestProducts = (limit = 8) => {
        return [...all_product]
            .sort((a, b) => b.id - a.id)
            .slice(0, limit);
    };

    // Function to get featured products
    const getFeaturedProducts = (limit = 8) => {
        return all_product
            .filter(product => product.isFeatured)
            .slice(0, limit);
    };

    // Function to get discounted products
    const getDiscountedProducts = (limit = 8) => {
        return all_product
            .filter(product => product.old_price > product.new_price)
            .sort((a, b) => {
                const discountA = ((a.old_price - a.new_price) / a.old_price) * 100;
                const discountB = ((b.old_price - b.new_price) / b.old_price) * 100;
                return discountB - discountA;
            })
            .slice(0, limit);
    };

    // Function to get similar products
    const getSimilarProducts = (product, limit = 4) => {
        return all_product
            .filter(p => 
                p.id !== product.id && 
                p.category === product.category
            )
            .slice(0, limit);
    };

    const contextValue = {
        // Cart related
        getTotalCartItems, 
        getTotalCartAmount, 
        cartItems, 
        addToCart, 
        removeFromCart,
        updateCartFromBackend,
        clearCart,
        getCartItemsDetails,
        getCartItemQuantity,
        getCartKey,
        getProductIdFromKey,
        placeOrder,
        getDetailedCartItems,
        cartCount,
        fetchCartCount,
        
        // Products related
        all_product, 
        allProducts,
        loading,
        error,
        clearError,
        showMessage,
        
        // Search functions
        searchProducts,
        getSearchSuggestions,
        performClientSideSearch,
        
        // Product utility functions
        getProductById,
        getProductsByCategory,
        getLatestProducts,
        getFeaturedProducts,
        getDiscountedProducts,
        getSimilarProducts,
        
        // Refresh function
        refreshAllProducts,
        
        // NEW: Wishlist functions
        wishlist,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        fetchWishlist,
        
        // NEW: Recently viewed
        recentlyViewed,
        addToRecentlyViewed
    };
    
    return (
        <ShopContext.Provider value={contextValue}>
            {props.children}
        </ShopContext.Provider>
    )
}

export default ShopContextProvider;

